import { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { toast } from "sonner"
import { useTranslation } from "@/hooks/use-translation"
import { GameLayout, GameStatusAlert } from '@/components/games/common'
import { GameShortcuts } from '@/components/games/common/game-shortcuts'
import { Grid, Lightbulb, Eraser } from 'lucide-react'
import { saveGameResult, getUserStats, getGameResult, hasPlayedToday } from '@/lib/game-stats'
import { InContentAd } from '@/components/adsense'
import { getDateString } from '@/lib/dictionaries'

interface GameState {
  grid: number[][]  // 9x9 grid con valores 0-9 (0 = vacío)
  notes: number[][][]  // 9x9 grid con arrays de números provisionales
  solution: number[][]  // Solución completa
  initialGrid: number[][]  // Grid inicial inmutable
  selectedCell: { row: number; col: number } | null
  gameStatus: 'playing' | 'won'
  hintsUsed: number
  startTime: number
  endTime: number | null
  notesMode: boolean  // Modo para introducir notas provisionales
}

// Función para generar un Sudoku 9x9 del día
function generateDailySudoku(language: string, dateOverride?: string): { puzzle: number[][], solution: number[][] } {
  const dateString = dateOverride || getDateString(new Date())



  // Crear un seed basado en la fecha y el idioma
  let seed = 0
  const seedString = dateString + language
  for (let i = 0; i < seedString.length; i++) {
    seed = ((seed << 5) - seed + seedString.charCodeAt(i)) & 0xffffffff
  }

  // Usar el seed para generar el Sudoku
  const random = () => {
    seed = ((seed * 1103515245 + 12345) & 0x7fffffff)
    return seed / 0x7fffffff
  }

  // Plantillas base de sudoku 9x9 válidas
  const baseTemplate = [
    [5, 3, 4, 6, 7, 8, 9, 1, 2],
    [6, 7, 2, 1, 9, 5, 3, 4, 8],
    [1, 9, 8, 3, 4, 2, 5, 6, 7],
    [8, 5, 9, 7, 6, 1, 4, 2, 3],
    [4, 2, 6, 8, 5, 3, 7, 9, 1],
    [7, 1, 3, 9, 2, 4, 8, 5, 6],
    [9, 6, 1, 5, 3, 7, 2, 8, 4],
    [2, 8, 7, 4, 1, 9, 6, 3, 5],
    [3, 4, 5, 2, 8, 6, 1, 7, 9]
  ]

  // Transformar la plantilla base según el seed
  const solution = baseTemplate.map(row => [...row])

  // Aplicar transformaciones determinísticas basadas en el seed
  const transforms = Math.floor(random() * 8)

  // Intercambiar filas dentro de grupos
  for (let i = 0; i < transforms; i++) {
    const group = Math.floor(random() * 3)
    const row1 = group * 3 + Math.floor(random() * 3)
    const row2 = group * 3 + Math.floor(random() * 3)
    if (row1 !== row2) {
      [solution[row1], solution[row2]] = [solution[row2], solution[row1]]
    }
  }

  // Intercambiar columnas dentro de grupos
  for (let i = 0; i < transforms; i++) {
    const group = Math.floor(random() * 3)
    const col1 = group * 3 + Math.floor(random() * 3)
    const col2 = group * 3 + Math.floor(random() * 3)
    if (col1 !== col2) {
      for (let row = 0; row < 9; row++) {
        [solution[row][col1], solution[row][col2]] = [solution[row][col2], solution[row][col1]]
      }
    }
  }

  // Crear puzzle quitando celdas (dificultad fácil-media)
  const puzzle = solution.map(row => [...row])
  const cellsToRemove = 35 + Math.floor(random() * 10) // Entre 35-45 celdas vacías (fácil)

  for (let i = 0; i < cellsToRemove; i++) {
    let row, col
    do {
      row = Math.floor(random() * 9)
      col = Math.floor(random() * 9)
    } while (puzzle[row][col] === 0)

    puzzle[row][col] = 0
  }

  return { puzzle, solution }
}

function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

export function SudokuDiario() {
  const { t, language } = useTranslation()

  // Generar puzzle del día
  const dailyPuzzle = generateDailySudoku(language)

  // Calcular información del puzzle
  const emptyCell = dailyPuzzle.puzzle.flat().filter(cell => cell === 0).length
  const difficulty = emptyCell <= 35 ? t("difficulty.easy") : emptyCell <= 40 ? t("difficulty.medium") : t("difficulty.hard")

  const [gameState, setGameState] = useState<GameState>({
    grid: dailyPuzzle.puzzle.map(row => [...row]),
    notes: Array(9).fill(null).map(() => Array(9).fill(null).map(() => [])),
    solution: dailyPuzzle.solution,
    initialGrid: dailyPuzzle.puzzle.map(row => [...row]),
    selectedCell: null,
    gameStatus: 'playing',
    hintsUsed: 0,
    startTime: 0,
    endTime: null,
    notesMode: false
  })

  // Nuevo estado para controlar si estamos en modo "nuevo juego"
  const [, setIsNewGameMode] = useState(false)

  // Estado para controlar el modal de primera vez en móvil
  const [showFirstTimeModal, setShowFirstTimeModal] = useState(false)

  // Estado para controlar el modo aprendizaje
  const [learningMode, setLearningMode] = useState(false)

  const today = getDateString()
  const [, setStats] = useState(getUserStats(`sudoku-diario-${language}`))
  const [currentTime, setCurrentTime] = useState(0)
  const timerRef = useRef<NodeJS.Timeout | null>(null)



  // Función para guardar el estado parcial del juego
  const savePartialGameState = (state: GameState) => {
    if (state.gameStatus === 'playing') {
      const partialState = {
        grid: state.grid,
        notes: state.notes,
        selectedCell: state.selectedCell,
        hintsUsed: state.hintsUsed,
        startTime: state.startTime,
        notesMode: state.notesMode,
        date: today
      }
      localStorage.setItem(`game-sudoku-diario-${language}-partial`, JSON.stringify(partialState))
    }
  }

  // Función para cargar el estado parcial del juego
  const loadPartialGameState = (): Partial<GameState> | null => {
    const key = `game-sudoku-diario-${language}-partial`
    const stored = localStorage.getItem(key)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        // Verificar que la fecha guardada coincida con el día actual
        if (parsed.date === today) {
          return parsed
        } else {
          // Si la fecha no coincide, limpiar el estado parcial
          localStorage.removeItem(key)
          return null
        }
      } catch {
        return null
      }
    }
    return null
  }

  // Función para limpiar el estado parcial (cuando se completa el juego)
  const clearPartialGameState = () => {
    localStorage.removeItem(`game-sudoku-diario-${language}-partial`)
  }

  // Función para cargar el estado final (cuando el juego está ganado)
  const loadFinalGameState = (): Partial<GameState> | null => {
    const key = `game-sudoku-diario-${language}-final`
    const stored = localStorage.getItem(key)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        // Verificar que la fecha guardada coincida con el día actual
        if (parsed.date === today) {
          return parsed
        } else {
          // Si la fecha no coincide, limpiar el estado final
          localStorage.removeItem(key)
          return null
        }
      } catch {
        return null
      }
    }
    return null
  }

  useEffect(() => {
    // Cargar estadísticas del localStorage específicas por idioma
    setStats(getUserStats(`sudoku-diario-${language}`))

    // Verificar si ya se jugó hoy
    if (hasPlayedToday(`sudoku-diario-${language}`, today)) {
      const todayResult = getGameResult(`sudoku-diario-${language}`, today)
      // Solo considerar el resultado si fue ganado, ignore las pérdidas del sistema anterior
      if (todayResult && todayResult.won) {
        // Usar los datos reales guardados (tiempo y pistas)
        const savedTime = todayResult.time || 0
        const savedHints = todayResult.hintsUsed || 0

        setGameState(prev => ({
          ...prev,
          gameStatus: 'won',
          endTime: savedTime,
          hintsUsed: savedHints
        }))
        // Cargar el estado final del grid cuando el juego está ganado
        const finalState = loadFinalGameState()
        if (finalState && finalState.grid && finalState.grid.length > 0) {
          setGameState(prev => ({
            ...prev,
            grid: finalState.grid as number[][],
            notes: finalState.notes || Array(9).fill(null).map(() => Array(9).fill(null).map(() => [])),
            selectedCell: null,
            notesMode: false
          }))
        } else {
          // Si no hay estado guardado, usar la solución completa
          setGameState(prev => ({
            ...prev,
            grid: [...prev.solution.map(row => [...row])],
            notes: Array(9).fill(null).map(() => Array(9).fill(null).map(() => [])),
            selectedCell: null,
            notesMode: false
          }))
        }
      } else {
        // Si no ganó o no hay resultado válido, intentar cargar estado parcial
        const partialState = loadPartialGameState()
        if (partialState) {
          setGameState(prev => ({
            ...prev,
            grid: partialState.grid || prev.grid,
            notes: partialState.notes || Array(9).fill(null).map(() => Array(9).fill(null).map(() => [])),
            selectedCell: partialState.selectedCell || null,
            hintsUsed: partialState.hintsUsed || 0,
            startTime: partialState.startTime || Date.now(),
            notesMode: partialState.notesMode || false
          }))
        } else {
          // Si no hay estado parcial, iniciar nuevo juego
          setGameState(prev => ({
            ...prev,
            startTime: Date.now()
          }))
        }
      }
    } else {
      // Intentar cargar estado parcial si no se ha completado el juego
      const partialState = loadPartialGameState()
      if (partialState) {
        setGameState(prev => ({
          ...prev,
          grid: partialState.grid || prev.grid,
          notes: partialState.notes || Array(9).fill(null).map(() => Array(9).fill(null).map(() => [])),
          selectedCell: partialState.selectedCell || null,
          hintsUsed: partialState.hintsUsed || 0,
          startTime: partialState.startTime || Date.now(),
          notesMode: partialState.notesMode || false
        }))
      } else {
        // Iniciar nuevo juego
        setGameState(prev => ({
          ...prev,
          startTime: Date.now()
        }))
      }
    }

    // Verificar si es la primera vez en móvil
    const hasSeenInstructions = localStorage.getItem(`game-sudoku-diario-${language}-instructions`)
    if (!hasSeenInstructions && window.innerWidth < 1024) { // lg breakpoint
      setShowFirstTimeModal(true)
    }
  }, [language, today])

  // Efecto para guardar el estado parcial cuando cambie el gameState
  useEffect(() => {
    if (gameState.gameStatus === 'playing') {
      savePartialGameState(gameState)
    }
  }, [gameState])

  // Timer effect
  useEffect(() => {
    if (gameState.gameStatus === 'playing' && gameState.startTime > 0) {
      timerRef.current = setInterval(() => {
        setCurrentTime(Math.floor((Date.now() - gameState.startTime) / 1000))
      }, 1000)
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [gameState.gameStatus, gameState.startTime])

  // Efecto para regenerar puzzle cuando cambia la fecha simulada
  useEffect(() => {
    // Solo regenerar el puzzle si no se ha completado el juego hoy
    if (!hasPlayedToday(`sudoku-diario-${language}`, today)) {
      const newPuzzle = generateDailySudoku(language)
      setGameState({
        grid: newPuzzle.puzzle.map(row => [...row]),
        notes: Array(9).fill(null).map(() => Array(9).fill(null).map(() => [])),
        solution: newPuzzle.solution,
        initialGrid: newPuzzle.puzzle.map(row => [...row]),
        selectedCell: null,
        gameStatus: 'playing',
        hintsUsed: 0,
        startTime: Date.now(),
        endTime: null,
        notesMode: false
      })
    }
  }, [language, today])

  const isValidMove = (grid: number[][], row: number, col: number, num: number): boolean => {
    // Verificar fila
    for (let c = 0; c < 9; c++) {
      if (c !== col && grid[row][c] === num) return false
    }

    // Verificar columna
    for (let r = 0; r < 9; r++) {
      if (r !== row && grid[r][col] === num) return false
    }

    // Verificar cuadrante 3x3
    const startRow = Math.floor(row / 3) * 3
    const startCol = Math.floor(col / 3) * 3
    for (let r = startRow; r < startRow + 3; r++) {
      for (let c = startCol; c < startCol + 3; c++) {
        if ((r !== row || c !== col) && grid[r][c] === num) return false
      }
    }

    return true
  }

  // Función para obtener números disponibles para la celda seleccionada
  const getAvailableNumbers = (): number[] => {
    if (!gameState.selectedCell) return []

    const { row, col } = gameState.selectedCell

    // No permitir editar celdas iniciales
    if (gameState.initialGrid[row][col] !== 0) return []

    const available: number[] = []
    for (let num = 1; num <= 9; num++) {
      if (isValidMove(gameState.grid, row, col, num)) {
        available.push(num)
      }
    }

    return available
  }

  const isSolved = (grid: number[][]): boolean => {
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        if (grid[row][col] === 0) return false
        if (grid[row][col] !== gameState.solution[row][col]) return false
      }
    }
    return true
  }

  const handleCellClick = (row: number, col: number) => {
    // No permitir editar celdas iniciales
    if (gameState.initialGrid[row][col] !== 0) return
    if (gameState.gameStatus !== 'playing') return

    setGameState(prev => ({
      ...prev,
      selectedCell: { row, col }
    }))
  }

  const handleNumberClick = (num: number) => {
    if (!gameState.selectedCell) return
    if (gameState.gameStatus !== 'playing') return

    const { row, col } = gameState.selectedCell

    // No permitir editar celdas iniciales
    if (gameState.initialGrid[row][col] !== 0) return

    if (gameState.notesMode) {
      // Modo notas: añadir/quitar números provisionales
      const newNotes = gameState.notes.map(noteRow => noteRow.map(noteCell => [...noteCell]))
      const currentNotes = newNotes[row][col]

      if (currentNotes.includes(num)) {
        // Quitar el número si ya existe
        newNotes[row][col] = currentNotes.filter(n => n !== num)
      } else {
        // Añadir el número si no existe
        newNotes[row][col] = [...currentNotes, num].sort()
      }

      setGameState(prev => ({
        ...prev,
        notes: newNotes
      }))
    } else {
      // Modo normal: colocar número definitivo
      const newGrid = gameState.grid.map(r => [...r])
      const newNotes = gameState.notes.map(noteRow => noteRow.map(noteCell => [...noteCell]))

      newGrid[row][col] = num
      // Limpiar las notas de esta celda cuando se coloca un número definitivo
      newNotes[row][col] = []

      // Solo verificar validez en modo aprendizaje
      if (learningMode && num !== 0 && !isValidMove(newGrid, row, col, num)) {
        toast.error(t("games.sudokuDiario.invalidMove"))
        return
      }

      // Movimiento permitido (válido o en modo normal)
      setGameState(prev => ({
        ...prev,
        grid: newGrid,
        notes: newNotes
      }))

      // Verificar si está resuelto
      if (isSolved(newGrid)) {
        const finalTime = Math.floor((Date.now() - gameState.startTime) / 1000)
        setGameState(prev => ({
          ...prev,
          gameStatus: 'won',
          endTime: finalTime
        }))

        // Guardar resultado con tiempo y pistas usadas
        saveGameResult(`sudoku-diario-${language}`, [], true, today, {
          time: finalTime,
          hintsUsed: gameState.hintsUsed
        })
        setStats(getUserStats(`sudoku-diario-${language}`))
        // Resetear modo nuevo juego al completar una partida
        setIsNewGameMode(false)
        // Guardar el estado final antes de limpiar
        const finalState = {
          grid: newGrid,
          notes: newNotes,
          selectedCell: null,
          hintsUsed: gameState.hintsUsed,
          startTime: gameState.startTime,
          notesMode: false,
          date: today
        }
        localStorage.setItem(`game-sudoku-diario-${language}-final`, JSON.stringify(finalState))
        clearPartialGameState()

        toast.success(t("games.sudokuDiario.congratulations", {
          time: formatTime(finalTime),
          hints: gameState.hintsUsed
        }), {
          duration: 4000
        })
      }
    }
  }

  const handleHint = () => {
    if (gameState.hintsUsed >= 100) return // Sin límite de pistas
    if (!gameState.selectedCell) return

    const { row, col } = gameState.selectedCell
    if (gameState.initialGrid[row][col] !== 0) return

    const correctNumber = gameState.solution[row][col]
    const currentNumber = gameState.grid[row][col]

    // Si ya tiene el número correcto, no contar como pista
    if (currentNumber === correctNumber) {
      toast.info("¡Esta celda ya tiene el número correcto!", {
        duration: 2000
      })
      return
    }

    const newGrid = gameState.grid.map(r => [...r])
    const newNotes = gameState.notes.map(noteRow => noteRow.map(noteCell => [...noteCell]))

    newGrid[row][col] = correctNumber
    // Limpiar las notas de esta celda cuando se coloca una pista
    newNotes[row][col] = []

    setGameState(prev => ({
      ...prev,
      grid: newGrid,
      notes: newNotes,
      hintsUsed: prev.hintsUsed + 1
    }))

    if (isSolved(newGrid)) {
      const finalTime = Math.floor((Date.now() - gameState.startTime) / 1000)
      setGameState(prev => ({
        ...prev,
        gameStatus: 'won',
        endTime: finalTime
      }))

      // Guardar resultado con tiempo y pistas usadas
      saveGameResult(`sudoku-diario-${language}`, [], true, today, {
        time: finalTime,
        hintsUsed: gameState.hintsUsed + 1
      })
      setStats(getUserStats(`sudoku-diario-${language}`))
      // Resetear modo nuevo juego al completar una partida
      setIsNewGameMode(false)
      // Guardar el estado final antes de limpiar
      const finalState = {
        grid: newGrid,
        notes: newNotes,
        selectedCell: null,
        hintsUsed: gameState.hintsUsed + 1,
        startTime: gameState.startTime,
        notesMode: false,
        date: today
      }
      localStorage.setItem(`game-sudoku-diario-${language}-final`, JSON.stringify(finalState))
      clearPartialGameState()

      toast.success(t("games.sudokuDiario.congratulations", {
        time: formatTime(finalTime),
        hints: gameState.hintsUsed + 1
      }), {
        duration: 4000
      })
    }
  }

  const handleClear = () => {
    if (!gameState.selectedCell) return
    if (gameState.gameStatus !== 'playing') return

    const { row, col } = gameState.selectedCell
    if (gameState.initialGrid[row][col] !== 0) return

    const newGrid = gameState.grid.map(r => [...r])
    const newNotes = gameState.notes.map(noteRow => noteRow.map(noteCell => [...noteCell]))

    newGrid[row][col] = 0
    newNotes[row][col] = []

    setGameState(prev => ({
      ...prev,
      grid: newGrid,
      notes: newNotes
    }))
  }







  const handleCloseFirstTimeModal = () => {
    localStorage.setItem(`game-sudoku-diario-${language}-instructions`, 'true')
    setShowFirstTimeModal(false)
  }

  const renderGameContent = () => (
    <>
      {/* Game Status - Show above the game board */}
      {gameState.gameStatus === 'won' && (
        <GameStatusAlert status="won">
          <div className="space-y-1">
            <div className="font-bold">
              {t("games.sudokuDiario.congratulations", {
                time: formatTime(gameState.endTime || 0),
                hints: gameState.hintsUsed
              })}
            </div>
            <div className="text-xs font-medium">
              {t("games.sudokuDiario.comeBackTomorrow")}
            </div>
          </div>
        </GameStatusAlert>
      )}

      {/* Sudoku Grid */}
      <div className="max-w-md mx-auto mb-4 lg:mb-6">
        <div className={`grid grid-cols-3 ${gameState.gameStatus === 'playing' ? 'gap-2 lg:gap-3' : 'gap-1 lg:gap-2'} bg-background p-2 lg:p-4 rounded-xl shadow-xl border-2 border-border`}>
          {/* Renderizar cada cuadrante 3x3 */}
          {Array.from({ length: 9 }, (_, quadrantIndex) => {
            const quadrantRow = Math.floor(quadrantIndex / 3)
            const quadrantCol = quadrantIndex % 3

            return (
              <div key={quadrantIndex} className={`grid grid-cols-3 ${gameState.gameStatus === 'playing' ? 'gap-0.5 lg:gap-1' : 'gap-0 lg:gap-0.5'} bg-muted p-1 lg:p-2 rounded-lg shadow-sm border border-border`}>
                {Array.from({ length: 9 }, (_, cellIndex) => {
                  const cellRow = Math.floor(cellIndex / 3)
                  const cellCol = cellIndex % 3
                  const actualRow = quadrantRow * 3 + cellRow
                  const actualCol = quadrantCol * 3 + cellCol
                  // Siempre mostrar los números del grid del usuario (incluyendo cuando está completado)
                  const cell = gameState.grid[actualRow][actualCol]

                  const isInitial = gameState.initialGrid[actualRow][actualCol] !== 0
                  const isSelected = gameState.selectedCell?.row === actualRow && gameState.selectedCell?.col === actualCol
                  const isInSameRow = gameState.selectedCell?.row === actualRow
                  const isInSameCol = gameState.selectedCell?.col === actualCol
                  const isInSameQuadrant = gameState.selectedCell &&
                    Math.floor(gameState.selectedCell.row / 3) === quadrantRow &&
                    Math.floor(gameState.selectedCell.col / 3) === quadrantCol

                  return (
                    <button
                      key={`${actualRow}-${actualCol}`}
                      className={`
                        ${gameState.gameStatus === 'playing' ? 'w-8 h-8 lg:w-9 lg:h-9' : 'w-6 h-6 lg:w-7 lg:h-7'} 
                        flex items-center justify-center font-semibold
                        ${gameState.gameStatus === 'playing' ? 'text-xs lg:text-sm' : 'text-[10px] lg:text-xs'}
                        transition-all duration-200 rounded-md relative
                        ${gameState.gameStatus === 'won'
                          ? (isInitial
                            ? 'bg-muted text-foreground border-border'
                            : 'bg-success text-success-foreground border-success')
                          : isInitial
                            ? 'bg-muted text-foreground cursor-not-allowed shadow-inner border border-border'
                            : 'bg-card text-card-foreground hover:bg-accent cursor-pointer shadow-sm border border-border hover:shadow-md'
                        }
                        ${gameState.gameStatus === 'playing' && isSelected ? 'ring-2 ring-primary bg-primary/20 scale-105 shadow-lg' : ''}
                        ${gameState.gameStatus === 'playing' && (isInSameRow || isInSameCol) ? 'bg-primary/10' : ''}
                        ${gameState.gameStatus === 'playing' && isInSameQuadrant && !isSelected && !isInSameRow && !isInSameCol ? 'bg-accent' : ''}
                      `}
                      onClick={() => handleCellClick(actualRow, actualCol)}
                      disabled={gameState.gameStatus !== 'playing'}
                    >
                      {cell !== 0 ? (
                        // Número definitivo
                        <span className="text-sm lg:text-base font-bold">{cell}</span>
                      ) : gameState.notes[actualRow][actualCol].length > 0 ? (
                        // Notas provisionales
                        <div className="grid grid-cols-3 gap-0 w-full h-full text-[7px] lg:text-[8px] font-normal">
                          {Array.from({ length: 9 }, (_, i) => {
                            const noteNumber = i + 1
                            const hasNote = gameState.notes[actualRow][actualCol].includes(noteNumber)
                            return (
                              <div
                                key={noteNumber}
                                className={`flex items-center justify-center ${hasNote ? 'text-muted-foreground' : 'opacity-0'}`}
                              >
                                {hasNote ? noteNumber : ''}
                              </div>
                            )
                          })}
                        </div>
                      ) : (
                        // Celda vacía
                        ''
                      )}
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>

      {/* Number Buttons */}
      {gameState.gameStatus === 'playing' && (
        <div className="mb-4 lg:mb-6 max-w-md mx-auto">
          {/* Layout: números 3x3 a la izquierda + botones de control a la derecha */}
          <div className="flex justify-center gap-4">
            {/* Números en 3 filas de 3 */}
            <div className="grid grid-cols-3 gap-2">
              {[
                [1, 2, 3],
                [4, 5, 6],
                [7, 8, 9]
              ].map((row) =>
                row.map((num) => {
                  let isAvailable = true
                  let buttonVariant: "outline" | "secondary" = "outline"

                  if (learningMode && !gameState.notesMode) {
                    const availableNumbers = getAvailableNumbers()
                    isAvailable = availableNumbers.includes(num)
                    if (!isAvailable) {
                      buttonVariant = "secondary"
                    }
                  }

                  return (
                    <Button
                      key={num}
                      variant={buttonVariant}
                      size="sm"
                      className="w-10 h-10 lg:w-12 lg:h-12 text-sm font-bold rounded-lg shadow-sm transition-all duration-200"
                      onClick={() => handleNumberClick(num)}
                      disabled={!gameState.selectedCell || (learningMode && !gameState.notesMode && !isAvailable)}
                    >
                      {num}
                    </Button>
                  )
                })
              )}
            </div>

            {/* Botones de control en columna a la derecha */}
            <div className="grid grid-cols-1 gap-2">
              {/* Botón modo notas */}
              <Button
                variant={gameState.notesMode ? "default" : "outline"}
                size="sm"
                className="w-20 h-10 lg:w-24 lg:h-12 text-xs lg:text-sm font-bold rounded-lg transition-all duration-200"
                onClick={() => setGameState(prev => ({ ...prev, notesMode: !prev.notesMode }))}
              >
                {gameState.notesMode ? '📝 Notas' : '✏️ Normal'}
              </Button>

              {/* Botón pista */}
              <Button
                variant="outline"
                size="sm"
                className="w-20 h-10 lg:w-24 lg:h-12 text-xs lg:text-sm font-bold rounded-lg shadow-sm transition-all duration-200"
                onClick={handleHint}
                disabled={gameState.hintsUsed >= 100 || !gameState.selectedCell}
              >
                <Lightbulb className="h-3 w-3 mr-1" />
                Pista
              </Button>

              {/* Botón borrar */}
              <Button
                variant="outline"
                size="sm"
                className="w-20 h-10 lg:w-24 lg:h-12 text-xs lg:text-sm font-bold rounded-lg shadow-sm transition-all duration-200"
                onClick={handleClear}
                disabled={!gameState.selectedCell}
              >
                <Eraser className="h-3 w-3 mr-1" />
                Borrar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Game Info - Movido aquí abajo */}
      <div className="text-center mb-3 lg:mb-6">
        {/* Info del juego en una sola línea */}
        <div className="flex justify-center items-center gap-4 lg:gap-6 mb-2 lg:mb-4 text-xs lg:text-sm">
          <div>
            <span className="text-muted-foreground">Dificultad:</span>
            <span className="ml-1 font-medium">{difficulty}</span>
          </div>
          <div>
            <span className="text-muted-foreground">{t("games.sudokuDiario.hints")}:</span>
            <span className="ml-1 font-bold">{gameState.hintsUsed}</span>
          </div>
          <div>
            <span className="text-muted-foreground">{t("games.sudokuDiario.time")}:</span>
            <span className="ml-1 font-bold">{formatTime(gameState.gameStatus === 'won' ? (gameState.endTime || 0) : currentTime)}</span>
          </div>
        </div>

        {/* Toggle para modo aprendizaje - Solo mostrar cuando está jugando */}
        {gameState.gameStatus === 'playing' && (
          <>
            <div className="flex justify-center items-center gap-2 mb-2 lg:mb-3">
              <span className="text-[10px] lg:text-xs text-muted-foreground">{t("games.sudokuDiario.normalMode")}</span>
              <button
                onClick={() => setLearningMode(!learningMode)}
                className={`relative inline-flex h-4 w-7 lg:h-5 lg:w-9 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${learningMode ? 'bg-primary' : 'bg-muted'
                  }`}
              >
                <span
                  className={`inline-block h-2.5 w-2.5 lg:h-3 lg:w-3 transform rounded-full bg-white transition-transform duration-200 ${learningMode ? 'translate-x-4 lg:translate-x-5' : 'translate-x-0.5 lg:translate-x-1'
                    }`}
                />
              </button>
              <span className="text-[10px] lg:text-xs text-muted-foreground">{t("games.sudokuDiario.learningMode")}</span>
            </div>

            {learningMode && (
              <div className="text-[10px] lg:text-xs text-primary">
                {t("games.sudokuDiario.learningModeActive")}
              </div>
            )}
          </>
        )}
      </div>
    </>
  )



  return (
    <GameLayout>
      {/* Desktop Layout */}
      <div className="hidden lg:flex lg:flex-row lg:gap-8 lg:px-8 lg:py-8">
        <div className="flex-1 flex flex-col justify-center items-center">
          <div className={`w-full ${gameState.gameStatus === 'won' ? 'max-w-lg' : 'max-w-xl xl:max-w-2xl'}`}>
            {renderGameContent()}
          </div>
        </div>

        {/* Sidebar - Desktop only - Siempre mostrar reglas */}
        <div className="w-80 min-w-80 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Grid className="h-4 w-4" />
                {t("games.sudokuDiario.howToPlay")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs">{t("games.sudokuDiario.rules.main")}</p>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Badge className="bg-primary text-primary-foreground text-xs">{t("games.sudokuDiario.rules.rows")}</Badge>
                  <span className="text-xs">{t("games.sudokuDiario.rules.rowsDesc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-secondary text-secondary-foreground text-xs">{t("games.sudokuDiario.rules.columns")}</Badge>
                  <span className="text-xs">{t("games.sudokuDiario.rules.columnsDesc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-muted text-muted-foreground text-xs">{t("games.sudokuDiario.rules.quadrants")}</Badge>
                  <span className="text-xs">{t("games.sudokuDiario.rules.quadrantsDesc")}</span>
                </div>
              </div>

              {/* Button Instructions */}
              <div className="pt-2 border-t border-border">
                <p className="text-xs font-medium mb-2">{t("games.sudokuDiario.gameControls")}</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">✏️</Badge>
                    <span className="text-xs">{t("games.sudokuDiario.buttons.normalDesc")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">📝</Badge>
                    <span className="text-xs">{t("games.sudokuDiario.buttons.notesDesc")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">💡</Badge>
                    <span className="text-xs">{t("games.sudokuDiario.buttons.hintDesc")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">🧽</Badge>
                    <span className="text-xs">{t("games.sudokuDiario.buttons.clearDesc")}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Advertisement below "Cómo Jugar" on desktop */}
          <InContentAd className="w-full" compact />

          {/* Game Shortcuts - Always show on desktop */}
          <GameShortcuts currentGame="sudoku-diario" />
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden min-h-[calc(100dvh-64px)] flex flex-col">
        {/* Advertisement above game board on mobile - Más compacto */}
        <div className="flex-shrink-0">
          <InContentAd className="w-full" compact />
        </div>

        <div className="flex-1 flex flex-col px-2 py-1 overflow-y-auto">
          <div className={`w-full mx-auto py-4 ${gameState.gameStatus === 'won' ? 'max-w-xs' : 'max-w-sm'}`}>
            {renderGameContent()}

            {/* Game Shortcuts - Mobile */}
            {(gameState.gameStatus === 'won' || hasPlayedToday(`sudoku-diario-${language}`, today)) && (
              <GameShortcuts currentGame="sudoku-diario" />
            )}
          </div>
        </div>
      </div>

      {/* Modal de primera vez - Solo móvil */}
      <Dialog open={showFirstTimeModal} onOpenChange={setShowFirstTimeModal}>
        <DialogContent className="max-w-sm mx-auto mt-20">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Grid className="h-5 w-5" />
              {t("games.sudokuDiario.howToPlay")}
            </DialogTitle>
            <DialogDescription className="text-left space-y-3">
              <p className="text-sm">{t("games.sudokuDiario.rules.main")}</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-primary text-primary-foreground text-xs">{t("games.sudokuDiario.rules.rows")}</Badge>
                  <span className="text-xs">{t("games.sudokuDiario.rules.rowsDesc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-secondary text-secondary-foreground text-xs">{t("games.sudokuDiario.rules.columns")}</Badge>
                  <span className="text-xs">{t("games.sudokuDiario.rules.columnsDesc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-muted text-muted-foreground text-xs">{t("games.sudokuDiario.rules.quadrants")}</Badge>
                  <span className="text-xs">{t("games.sudokuDiario.rules.quadrantsDesc")}</span>
                </div>
              </div>

              {/* Button Instructions */}
              <div className="pt-2 border-t border-border">
                <p className="text-xs font-medium mb-2">{t("games.sudokuDiario.gameControls")}</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">✏️</Badge>
                    <span className="text-xs">{t("games.sudokuDiario.buttons.normalDesc")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">📝</Badge>
                    <span className="text-xs">{t("games.sudokuDiario.buttons.notesDesc")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">💡</Badge>
                    <span className="text-xs">{t("games.sudokuDiario.buttons.hintDesc")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">🧽</Badge>
                    <span className="text-xs">{t("games.sudokuDiario.buttons.clearDesc")}</span>
                  </div>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end pt-4">
            <Button onClick={handleCloseFirstTimeModal}>
              {t("navigation.gotIt")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </GameLayout>
  )
} 